import {createReducer} from "./index";
import {
  API_GET_USER_QUERY_DONE,
  API_GET_USER_QUERY_RESULTS,
  API_GET_USER_QUERY_START,
  CLEAR_USER_SEARCH
} from "../actions/users";
import {message as antMessage} from "antd";
import * as R from 'ramda';
import {
  CLEAR_EMULATED_USER,
  CLEAR_LOADING_FLAG,
  NETWORK_ERROR,
  SET_AUTH_DATA, SET_AUTH_STATE,
  SET_AUTHENTICATED_USER,
  SET_EMULATED_USER,
  SET_LOADING_ERROR,
  SET_LOADING_FLAG,
  UI_ERROR, UI_INFO, UI_SUCCESS
} from "../actions/ui";
import {createSelector} from "reselect/es"; // fix for Webstorm IDE code analysis
import {
  API_GET_COMPANY_JOIN_REQUESTS_DONE,
  API_GET_COMPANY_JOIN_REQUESTS_RESULTS,
  API_GET_COMPANY_JOIN_REQUESTS_START,
  API_GET_COMPANY_QUERY_DONE,
  API_GET_COMPANY_QUERY_RESULTS,
  API_GET_COMPANY_QUERY_START,
  CLEAR_COMPANY_SEARCH
} from "../actions/companies";


/**
 * Fetch the current effective user ID, if any. If emulating a user, this returns the emulated user ID; otherwise, the authenticated user ID, which may be unset.
 * @param state - redux state
 * @returns a UUID string of the effective user
 */
export const currentEffectiveUserId = state => state.ui.authData.emulatedUser || state.ui.authData.authenticatedUser;

/**
 * Fetch the current authenticated user ID, which may be unset.
 * @param state - redux state
 * @returns a UUID string of the authenticated user
 */
export const currentAuthenticatedUserId = state => state.ui.authData.authenticatedUser;

/**
 * Fetch the current emulated user ID, which may be unset.
 * @param state - redux state
 * @returns a UUID string of the emulated user
 */
export const currentEmulatedUserId = state => state.ui.authData.emulatedUser;

export const searchState = state => state.ui.userSearchState;

export const SET_NOTIFICATION_INTERVAL = 'SET_NOTIFICATION_INTERVAL';
export const setNotificationInterval = (interval) => ({ type: SET_NOTIFICATION_INTERVAL, payload: { interval }});

export const SET_ARCHIVE_PENDING = 'SET_ARCHIVE_PENDING';
export const setArchivePending = (uuid, data) => ({ type: SET_ARCHIVE_PENDING, payload: { uuid, data }});
export const CLEAR_ARCHIVE_PENDING = 'CLEAR_ARCHIVE_PENDING';
export const clearArchivePending = (uuid) => ({ type: CLEAR_ARCHIVE_PENDING, payload: { uuid }});

const uiReducer = createReducer({
    lastError: null,
    userSearchResults: [],
    userSearchState: 'complete',
    userSearchTerm: null,
    companySearchResults: [],
    companySearchState: 'complete',
    companySearchTerm: null,
    companyJoinRequests: [],
    /**
     * currentUser refers to the effective current user from the server: if emulating a user, this refers to the emulated user. Otherwise, this refers to the authenticated user, if any.
     */
    currentUser: null,
    emulatedUser: null,
    authState: 'signedOut',
    authData: null,
    notificationInterval: 10 * 1000,
    archivesPending: {}
  },
  {
    [SET_ARCHIVE_PENDING]: (state, action) => {
      const { uuid, data } = action.payload;
      return R.set(R.lensPath(['archivesPending', uuid]), data, state);
    },
    [CLEAR_ARCHIVE_PENDING]: (state, action) => {
      const { uuid } = action.payload;
      return R.dissocPath(['archivesPending', uuid], state);
    },
    [SET_NOTIFICATION_INTERVAL]: (state, action) => {
      const { interval } = action.payload;
      return R.set(R.lensProp('notificationInterval'), interval, state);
    },
    [UI_SUCCESS]: (state, action) => {
      const { content, location, message } = action.payload;
      antMessage.success(message);
      return { ...state, lastMessage: content };
    },
    [UI_INFO]: (state, action) => {
      const { content, location, message } = action.payload;
      antMessage.info(message);
      return { ...state, lastMessage: content };
    },
    [UI_ERROR]: (state, action) => {
      const { error, location, message } = action.payload;
      antMessage.error(message);
      return { ...state, lastError: error };
    },
    [NETWORK_ERROR]: (state, action) => {
      const { error, location, message } = action.payload;
      console.log(message, error, 'at', location, error && error.stack);
      // antMessage.error(message, 20);
      return { ...state, lastError: action.payload.error };
    },
    [SET_LOADING_FLAG]: (state, action) => {
      const { flag } = action.payload;
      return R.assoc(flag, 'loading', state);
    },
    [CLEAR_LOADING_FLAG]: (state, action) => {
      const { flag } = action.payload;
      return R.dissoc(flag, state);
    },
    [SET_LOADING_ERROR]: (state, action) => {
      const { flag, error } = action.payload;
      return R.assoc(flag, { error }, state);
    },
    [API_GET_USER_QUERY_START]: (state, action) => {
      state = removeUserRefsFromQueryView(state);
      state = R.set(R.lensProp('userSearchTerm'), action.payload.q, state);
      return R.set(R.lensProp('userSearchState'), 'started', state);
    },
    [API_GET_USER_QUERY_DONE]: (state, action) => {
      return R.set(R.lensProp('userSearchState'), 'complete', state);
    },
    [API_GET_USER_QUERY_RESULTS]: (state, action) => {
      const { results } = action.payload;
      return addUserRefsToQueryView(state, results);
    },
    [CLEAR_USER_SEARCH]: (state, action) => {
      state = R.set(R.lensProp('userSearchTerm'), null, state);
      return removeUserRefsFromQueryView(state);
    },
    [API_GET_COMPANY_QUERY_START]: (state, action) => {
      state = removeCompanyRefsFromQueryView(state);
      state = R.set(R.lensProp('companySearchTerm'), action.payload.q, state);
      return R.set(R.lensProp('companySearchState'), 'started', state);
    },
    [API_GET_COMPANY_QUERY_DONE]: (state, action) => {
      return R.set(R.lensProp('companySearchState'), 'complete', state);
    },
    [API_GET_COMPANY_QUERY_RESULTS]: (state, action) => {
      const { results } = action.payload;
      return addCompanyRefsToQueryView(state, results);
    },
    [CLEAR_COMPANY_SEARCH]: (state, action) => {
      state = R.set(R.lensProp('companySearchTerm'), null, state);
      return removeCompanyRefsFromQueryView(state);
    },
    [SET_AUTHENTICATED_USER]: (state, action) => {
      const { payload: { user }} = action;
      return R.set(R.lensProp('currentUser'), user, state);
    },
    [SET_AUTH_STATE]: (state, action) => {
      const { payload: { authState }} = action;
      return R.set(R.lensProp('authState'), authState, state);
    },
    [SET_AUTH_DATA]: (state, action) => {
      const { payload: { data }} = action;
      return R.set(R.lensProp('authData'), data, state);
    },
    [SET_EMULATED_USER]: (state, action) => {
      const { payload: { user }} = action;
      return R.set(R.lensProp('emulatedUser'), user, state);
    },
    [CLEAR_EMULATED_USER]: (state, action) => {
      return R.set(R.lensProp('emulatedUser'), null, state);
    },
    [API_GET_COMPANY_JOIN_REQUESTS_START]: (state, action) => {
      return removeJoinRequestsFromView(state);
    },
    [API_GET_COMPANY_JOIN_REQUESTS_RESULTS]: (state, action) => {
      const { results } = action.payload;
      return addJoinRequestsToView(state, results);
    },
    [API_GET_COMPANY_JOIN_REQUESTS_DONE]: (state, action) => {
      return state;
    }
  });

const removeUserRefsFromQueryView = (state) => {
  return R.set(R.lensProp('userSearchResults'), [], state);
};

const removeCompanyRefsFromQueryView = (state) => {
  return R.set(R.lensProp('companySearchResults'), [], state);
};

const addUserRefsToQueryView = (state, users) => {
  let newState = state;
  let userIds = users.map(x => x.id);
  newState = R.over(R.lensProp('userSearchResults'), currentIds => R.concat(currentIds, userIds), newState);
  return newState;
};

const addCompanyRefsToQueryView = (state, companies) => {
  // let companyIds = companies.map(x => x.id);
  return R.over(R.lensProp('companySearchResults'), current => R.concat(current, companies), state);
};

const addJoinRequestsToView = (state, requests) => {
  return R.over(R.lensProp('companyJoinRequests'), current => R.concat(current, requests), state);
};

const removeJoinRequestsFromView = (state) => {
  return R.set(R.lensProp('companyJoinRequests'), [], state);
};

export default uiReducer;



// WEBPACK FOOTER //
// ./src/reducers/ui.js